import axios, {
  AxiosHeaders,
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
  Method,
  RawAxiosRequestHeaders,
} from 'axios';
import { getAccessToken } from '@neotech-solutions-org/neofusion-fe-shared';
import { UserRole } from '../@types/api';
import { MINUTE } from '../constants';
import { getItemFromSessionStorage, setItemInSessionStorage } from './storageHelper';
import { APP_EVENT } from '../constants/events';
import { DYNAMIC_URL_TYPE, generateUrl, UrlType } from './envDynamicValues';
import { runtimeEnv } from '../config/runtimeEnv';
import { resolveRequestAuthorizationToken } from './casinoAuth';

const API_INACTIVITY_TIMEOUT = 30 * MINUTE;
const SUPER_ADMIN_DEFAULT_MARKETPLACE_ID = '00000001-0000-4000-8000-0000b01dface';
const SUPER_ADMIN_ROLE = 'default-super-admin-role';

export type MethodsHeaders = Partial<
  {
    [Key in Method as Lowercase<Key>]: AxiosHeaders;
  } & { common: AxiosHeaders }
>;

const handleMultiTenant = (config: InternalAxiosRequestConfig) => {
  const marketplaceInStorage = getItemFromSessionStorage('marketplace');

  if (marketplaceInStorage) {
    const lastRequestTime = getItemFromSessionStorage('lastRequestTime');
    const currentTime = new Date().getTime();

    if (lastRequestTime && currentTime - lastRequestTime > API_INACTIVITY_TIMEOUT) {
      window.dispatchEvent(new Event(APP_EVENT.INACTIVITY_TIMEOUT));
    } else {
      setItemInSessionStorage('lastRequestTime', currentTime);
    }
  }

  if (marketplaceInStorage?.id) {
    config.headers.set('marketplaceId', marketplaceInStorage.id);
  }
};

const parseToken = (token: string) => {
  const parts = token.split('.');
  return JSON.parse(atob(parts[1]));
};

const isSuperAdmin = (jwtToken: string) => {
  const parsedToken = parseToken(jwtToken);
  return parsedToken?.realm_access?.roles?.includes(SUPER_ADMIN_ROLE);
};

const getApiServiceFromBaseUrl = (baseURL?: string): ApiService | undefined => {
  return Object.entries(axiosInstances).find(([, instance]) => instance.defaults.baseURL === baseURL)?.[0] as
    | ApiService
    | undefined;
};

const getRequestSource = () => {
  return typeof window === 'undefined' ? undefined : window.location.origin;
};

/**
 * Casino API auth is a two-token flow:
 * 1. BO starts with the Keycloak/BO access token.
 * 2. `casino-api` accepts that token only on `auth/authenticate`, where DomainMiddleware
 *    maps the request to a marketplace and returns a casino session hash.
 * 3. Protected casino endpoints expect that casino session hash in `Authorization`.
 *
 * Sending the BO token directly to protected casino endpoints fails because
 * `casino-api` AuthMiddleware looks up Authorization as a casino cache key.
 */
const createCasinoSessionToken = async (token: string) => {
  const { data } = await axios.post<{ accessToken?: string }>(
    'auth/authenticate',
    { token },
    {
      baseURL: axiosInstances.casino.defaults.baseURL,
      headers: {
        source: getRequestSource(),
      },
    }
  );

  return data.accessToken;
};

const requestHandler = async (config: InternalAxiosRequestConfig) => {
  const { data, baseURL } = config;
  const keycloakUrl = runtimeEnv.keycloakUrl ?? generateUrl('bo');
  const isImpersonationRequest = baseURL === keycloakUrl;
  const apiService = getApiServiceFromBaseUrl(baseURL);
  const tokenData = isImpersonationRequest
    ? await postData<{ token?: string }>('/customer/impersonation', { userId: data.userId }, 'crm')
    : null;
  const boToken = tokenData?.token ?? (await getAccessToken());

  if (boToken) {
    if (isSuperAdmin(boToken)) {
      const selectedMarketplace = getItemFromSessionStorage('marketplace');
      config.headers.marketplaceId = selectedMarketplace?.id ?? SUPER_ADMIN_DEFAULT_MARKETPLACE_ID;
    }

    const authorizationToken = await resolveRequestAuthorizationToken({
      apiService,
      path: config.url,
      boToken,
      createCasinoSessionToken,
    });

    if (authorizationToken) {
      config.headers.Authorization = `Bearer ${authorizationToken}`;
    }
  }

  handleMultiTenant(config);

  return config;
};

const axiosConfigs = [
  {
    key: 'feed',
    envVar: runtimeEnv.apiFeedUrl,
    urlType: DYNAMIC_URL_TYPE.API,
    endpoint: 'sportsbook-feed-api',
  },
  {
    key: 'crm',
    envVar: runtimeEnv.apiCrmUrl,
    urlType: DYNAMIC_URL_TYPE.API,
    endpoint: 'sportsbook-crm-api',
  },
  {
    key: 'wallet',
    envVar: runtimeEnv.apiWalletUrl,
    urlType: DYNAMIC_URL_TYPE.API,
    endpoint: 'sb-wallet-api',
  },
  {
    key: 'casino',
    envVar: runtimeEnv.apiCasinoUrl,
    urlType: DYNAMIC_URL_TYPE.API,
    endpoint: 'casino-api',
  },
  {
    key: 'kc',
    envVar: runtimeEnv.keycloakUrl,
    urlType: DYNAMIC_URL_TYPE.BO,
    options: { withCredentials: true },
  },
  {
    key: 'ftbConsumer',
    envVar: runtimeEnv.apiFtbConsumerUrl,
    urlType: DYNAMIC_URL_TYPE.API,
    endpoint: 'ftb-consumer',
  },
];

const axiosInstances: Record<string, AxiosInstance> = {};

const createAxiosInstances = () => {
  axiosConfigs.forEach(({ key, envVar, urlType, endpoint, options }) => {
    const baseURL = envVar ?? generateUrl(urlType as UrlType, endpoint);
    const instance = axios.create({
      baseURL,
      ...options,
    });

    // Add request interceptor
    instance.interceptors.request.use(requestHandler);

    // Store the instance
    axiosInstances[key] = instance;
  });
};

createAxiosInstances();

type UserCredentials = {
  agentId: string;
  role: UserRole;
};

let cachedAgentCredentials: UserCredentials | null = null;
let agentCredentialsFetched = false;

export const getAgentCredentials = async () => {
  if (cachedAgentCredentials && agentCredentialsFetched) {
    return cachedAgentCredentials;
  }

  const { data } = await axiosInstances.crm.get('/agent/by-user', {
    headers: {
      marketplaceId: SUPER_ADMIN_DEFAULT_MARKETPLACE_ID,
    },
  });

  if (data && data.length > 0) {
    const { agentid: agentId, role } = data[0];

    cachedAgentCredentials = { agentId, role };
    agentCredentialsFetched = true;

    return cachedAgentCredentials;
  } else {
    return null;
  }
};

export type ApiService = keyof typeof axiosInstances;

const executeRequest = async <T = unknown>(
  method: 'get' | 'post' | 'put' | 'patch' | 'delete',
  path: string,
  body: unknown | null,
  apiService: ApiService = 'feed',
  queryParams?: unknown,
  headers?: (RawAxiosRequestHeaders & MethodsHeaders) | AxiosHeaders
): Promise<T> => {
  let updatedHeaders = headers;

  const agentCredentials = await getAgentCredentials();

  if (apiService === 'crm') {
    if (agentCredentials) {
      updatedHeaders = {
        ...headers,
        agentId: agentCredentials.agentId,
        role: agentCredentials.role,
      };
    }
  }

  const requestOptions: AxiosRequestConfig = {
    headers: { ...updatedHeaders },
    params: queryParams,
  };

  if (method === 'get' || method === 'delete') {
    const { data } = await axiosInstances[apiService][method](path, { ...requestOptions, data: body });
    return data as T;
  } else {
    const { data } = await axiosInstances[apiService][method](path, body, requestOptions);
    return data as T;
  }
};

export const getData = async <T = unknown>(
  path: string,
  queryParams?: unknown,
  apiService: ApiService = 'feed',
  headers?: (RawAxiosRequestHeaders & MethodsHeaders) | AxiosHeaders
): Promise<T> => {
  return executeRequest<T>('get', path, null, apiService, queryParams, headers);
};

export const postData = async <T = unknown>(
  path: string,
  body: unknown,
  apiService: ApiService = 'feed',
  headers?: (RawAxiosRequestHeaders & MethodsHeaders) | AxiosHeaders
): Promise<T> => {
  return executeRequest<T>('post', path, body, apiService, null, headers);
};

export const patchData = async <T = unknown>(
  path: string,
  body: unknown,
  apiService: ApiService = 'feed',
  headers?: (RawAxiosRequestHeaders & MethodsHeaders) | AxiosHeaders
): Promise<T> => {
  return executeRequest<T>('patch', path, body, apiService, null, headers);
};

export const deleteData = async <T = unknown>(
  path: string,
  apiService: ApiService = 'feed',
  headers?: (RawAxiosRequestHeaders & MethodsHeaders) | AxiosHeaders,
  body?: unknown | null
): Promise<T> => {
  return executeRequest<T>('delete', path, body, apiService, null, headers);
};

export const hasApiMorePages = (count: number, page: number, limit: number) => {
  const pagesCount = Math.ceil(count / limit);
  return page < pagesCount;
};
